import {
  useEffect,
  useRef,
  useState,
  useCallback,
  useDeferredValue,
  useImperativeHandle,
  forwardRef,
} from 'react';
import { cn } from '../../utils';

// ---- Three.js interop types (minimal, from the bundled three inside globe.gl) ----

interface ThreeUniform<T = number> {
  value: T;
}

interface ThreeVector3 {
  x: number;
  y: number;
  z: number;
}

interface ThreeColor {
  r: number;
  g: number;
  b: number;
}

interface ThreeMaterial {
  type: string;
  dispose: () => void;
  transparent?: boolean;
  opacity?: number;
  color?: ThreeColor;
  map?: { dispose: () => void };
  lightMap?: { dispose: () => void };
  bumpMap?: { dispose: () => void };
  normalMap?: { dispose: () => void };
  specularMap?: { dispose: () => void };
  envMap?: { dispose: () => void };
  uniforms?: Record<string, ThreeUniform<unknown>>;
  needsUpdate?: boolean;
}

interface ThreeShaderMaterialOpts {
  uniforms: Record<string, ThreeUniform<unknown>>;
  vertexShader: string;
  fragmentShader: string;
  transparent?: boolean;
  depthWrite?: boolean;
  side?: number;
}

type ThreeShaderMaterialConstructor = new (opts: ThreeShaderMaterialOpts) => ThreeMaterial;

interface ThreeGeometry {
  type: string;
  attributes: { position?: { count: number } };
  dispose: () => void;
}

interface ThreeObject3D {
  type: string;
  children: ThreeObject3D[];
  geometry?: ThreeGeometry;
  material?: ThreeMaterial | ThreeMaterial[];
  traverse: (fn: (obj: ThreeObject3D) => void) => void;
}

// ---- Globe.gl instance type ----
// Minimal interface covering only the methods we use. The full library types
// pull in all of three.js which conflicts with our shader interop types above.

type GlobeInstance = {
  globeImageUrl: (url: string) => GlobeInstance;
  backgroundColor: (color: string) => GlobeInstance;
  showAtmosphere: (show: boolean) => GlobeInstance;
  atmosphereColor: (color: string) => GlobeInstance;
  atmosphereAltitude: (altitude: number) => GlobeInstance;
  width: (width: number) => GlobeInstance;
  height: (height: number) => GlobeInstance;
  pointsData: (data: LandDot[]) => GlobeInstance;
  pointColor: (fn: () => string) => GlobeInstance;
  pointRadius: (radius: number) => GlobeInstance;
  pointResolution: (resolution: number) => GlobeInstance;
  pointAltitude: (altitude: number) => GlobeInstance;
  pointsMerge: (merge: boolean) => GlobeInstance;
  labelText: (fn: () => string) => GlobeInstance;
  labelColor: (fn: () => string) => GlobeInstance;
  labelDotRadius: (radius: number) => GlobeInstance;
  labelAltitude: (altitude: number) => GlobeInstance;
  labelsTransitionDuration: (duration: number) => GlobeInstance;
  labelsData: (data: GlobeCoordinate[]) => GlobeInstance;
  globeMaterial: () => {
    opacity: number;
    shininess: number;
    transparent: boolean;
    color: { set: (color: string) => void };
  };
  pointOfView: (
    view: { lat?: number; lng?: number; altitude?: number },
    transitionMs?: number
  ) => GlobeInstance;
  controls: () => {
    autoRotate: boolean;
    autoRotateSpeed: number;
    enabled: boolean;
    enableZoom: boolean;
  };
  scene: () => ThreeObject3D;
  onGlobeClick: (
    fn: (coords: { lat: number; lng: number }, event: MouseEvent) => void
  ) => GlobeInstance;
};

type GlobeConstructor = new (element: HTMLElement) => GlobeInstance;

export interface GlobeCoordinate {
  lat: number;
  lng: number;
}

export interface GlobeHandle {
  focusOn: (
    coord: GlobeCoordinate,
    opts?: { altitude?: number; transitionMs?: number; color?: string }
  ) => void;
  clearFocus: (opts?: { transitionMs?: number }) => void;
}

export interface GlobeProps {
  width?: number | 'auto';
  height?: number | 'auto';
  neutralColor?: string;
  atmosphereColor?: string;
  globeColor?: string;
  showAtmosphere?: boolean;
  autoRotateSpeed?: number;
  enableZoom?: boolean;
  interactive?: boolean;
  cameraAltitude?: number;
  landDotRows?: number;
  landMapUrl?: string;
  className?: string;
  onReady?: () => void;
  pointSize?: number;
  pointResolution?: number;
  atmosphereAltitude?: number;
  globeOpacity?: number;
  onGlobeClick?: (coords: GlobeCoordinate, event: MouseEvent) => void;
  focusCoordinate?: GlobeCoordinate | null;
  focusColor?: string;
  focusAltitude?: number;
  focusTransitionMs?: number;
}

interface LandDot {
  lat: number;
  lng: number;
}

const landDotsCache = new Map<string, LandDot[]>();

const DEG2RAD = Math.PI / 180;

function disposeMaterial(material: ThreeMaterial) {
  if (material.map) material.map.dispose();
  if (material.lightMap) material.lightMap.dispose();
  if (material.bumpMap) material.bumpMap.dispose();
  if (material.normalMap) material.normalMap.dispose();
  if (material.specularMap) material.specularMap.dispose();
  if (material.envMap) material.envMap.dispose();
  material.dispose();
}

function parseRgbTuple(color: string): [number, number, number] {
  const nums = color.match(/\d+/g);
  if (nums && nums.length >= 3) {
    return [parseInt(nums[0], 10), parseInt(nums[1], 10), parseInt(nums[2], 10)];
  }
  return [239, 68, 68];
}

function createColorTexture(color: string): string {
  const canvas = document.createElement('canvas');
  canvas.width = 1;
  canvas.height = 1;
  const ctx = canvas.getContext('2d');
  if (ctx) {
    ctx.fillStyle = color;
    ctx.fillRect(0, 0, 1, 1);
  }
  return canvas.toDataURL();
}

// ---- GPU ripple shader ----
// Gaussian wave packet with dispersion: pulse width grows as sqrt(1 + (d/d0)^2),
// amplitude decays as sigma0/sigma (energy conservation). Two overlapping pulses
// offset by half a cycle for seamless looping. Computed per-vertex with flat
// interpolation so each land dot gets a uniform intensity (no intra-dot gradients).

const RIPPLE_VERTEX_SHADER = `
  uniform vec3 focusDir;
  uniform float time;
  uniform float sigma0;
  uniform float dispersionLen;
  uniform float cycleDuration;
  uniform float maxFront;
  flat varying float vBand;

  const float PI = 3.14159265;

  // Deterministic hash from a 3D position -- gives each dot a stable random value
  float hash(vec3 p) {
    p = fract(p * vec3(443.897, 441.423, 437.195));
    p += dot(p, p.yzx + 19.19);
    return fract((p.x + p.y) * p.z);
  }

  // Gaussian pulse with distance-dependent width and amplitude decay
  float pulse(float dist, float front, float lifePhase) {
    float sigma = sigma0 * sqrt(1.0 + (front / dispersionLen) * (front / dispersionLen));
    float amplitude = sigma0 / sigma;
    float disp = dist - front;
    float envelope = exp(-disp * disp / (2.0 * sigma * sigma));
    return amplitude * envelope * sin(lifePhase * PI);
  }

  void main() {
    vec3 dir = normalize(position);
    float dist = acos(clamp(dot(dir, focusDir), -1.0, 1.0));

    float elapsed = time / cycleDuration;

    float t1 = fract(elapsed);
    float t2 = fract(elapsed - 0.5);
    float active2 = step(0.5, elapsed);

    float front1 = t1 * maxFront;
    float front2 = t2 * maxFront;

    float p1 = pulse(dist, front1, t1);
    float p2 = pulse(dist, front2, t2) * active2;

    float band = max(p1, p2);

    // Per-dot intensity jitter: hash gives each dot a stable random 0-1 value,
    // scaled to a +/-20% modulation so dots along the wavefront shimmer
    float jitter = 0.8 + 0.4 * hash(dir * 500.0);
    vBand = band * jitter;

    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;

const RIPPLE_FRAGMENT_SHADER = `
  uniform vec3 originalColor;
  uniform float originalAlpha;
  uniform float dimming;
  uniform vec3 waveColor;
  uniform float waveAlpha;
  flat varying float vBand;

  void main() {
    // Darken base dots during focus (dimming 0→1 multiplies toward black)
    vec3 base = originalColor * (1.0 - dimming);
    vec3 color = mix(base, waveColor, vBand);
    float alpha = mix(originalAlpha, waveAlpha, vBand);
    gl_FragColor = vec4(color, alpha);
  }
`;

/** Convert lat/lng to a normalized 3D direction matching the merged points geometry */
function latLngToDir(lat: number, lng: number): ThreeVector3 {
  const latR = lat * DEG2RAD;
  const lngR = lng * DEG2RAD;
  return {
    x: Math.cos(latR) * Math.sin(lngR),
    y: Math.sin(latR),
    z: Math.cos(latR) * Math.cos(lngR),
  };
}

// ---- Permanent shader state for land dots ----
// Applied once at init; ripple activates by animating `time` uniform,
// deactivates by resetting `time` to 0 (no material swapping).

interface RippleUniforms {
  focusDir: ThreeUniform<ThreeVector3>;
  time: ThreeUniform<number>;
  sigma0: ThreeUniform<number>;
  dispersionLen: ThreeUniform<number>;
  cycleDuration: ThreeUniform<number>;
  maxFront: ThreeUniform<number>;
  waveColor: ThreeUniform<ThreeVector3>;
  waveAlpha: ThreeUniform<number>;
  originalColor: ThreeUniform<ThreeVector3>;
  originalAlpha: ThreeUniform<number>;
  dimming: ThreeUniform<number>;
}

interface ShaderState {
  mesh: ThreeObject3D & { material: ThreeMaterial };
  originalMaterial: ThreeMaterial;
  material: ThreeMaterial;
  uniforms: RippleUniforms;
}

/** Lazy-load globe.gl via dynamic import so it's code-split from the main bundle */
async function loadGlobeModule(): Promise<GlobeConstructor> {
  // globe.gl ships as ESM with a default export (kapsule constructor)
  const mod = await import('globe.gl');
  return mod.default as unknown as GlobeConstructor;
}

// Live-updatable settings read via ref so the init effect avoids stale closures
// without needing these values in its dependency array
interface LiveSettings {
  showAtmosphere: boolean;
  atmosphereColor: string;
  atmosphereAltitude: number;
  globeColor: string;
  globeOpacity: number;
  autoRotateSpeed: number;
  enableZoom: boolean;
  interactive: boolean;
  cameraAltitude: number;
  neutralColor: string;
}

// ---- Component ----

export const Globe = forwardRef<GlobeHandle, GlobeProps>(function Globe(
  {
    width = 'auto',
    height = 'auto',
    neutralColor = 'rgb(26, 211, 121)',
    atmosphereColor: atmosphereColorProp,
    globeColor = 'rgb(26, 26, 26)',
    showAtmosphere = true,
    autoRotateSpeed = 0.85,
    enableZoom = false,
    interactive = true,
    cameraAltitude = 2,
    landDotRows = 350,
    pointSize = 0.3,
    atmosphereAltitude = 0.25,
    landMapUrl = 'https://assets.ot.digital/img/map.png',
    className,
    onReady,
    onGlobeClick,
    pointResolution = 4,
    globeOpacity = 1,
    focusCoordinate = null,
    focusColor = 'rgb(117, 240, 176)',
    focusAltitude = 0.9,
    focusTransitionMs = 1000,
  },
  ref
) {
  const containerRef = useRef<HTMLDivElement>(null);
  const globeRef = useRef<GlobeInstance | null>(null);
  const cleanupFnRef = useRef<(() => void) | null>(null);
  const isInitializingRef = useRef(false);
  const isFocusedRef = useRef(false);
  const shaderRef = useRef<ShaderState | null>(null);
  const rippleFrameRef = useRef<number | null>(null);
  const rippleDelayRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const fadeFrameRef = useRef<number | null>(null);
  const [GlobeClass, setGlobeClass] = useState<GlobeConstructor | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isGlobeVisible, setIsGlobeVisible] = useState(false);

  const onGlobeClickRef = useRef(onGlobeClick);
  useEffect(() => {
    onGlobeClickRef.current = onGlobeClick;
  }, [onGlobeClick]);

  const deferredNeutralColor = useDeferredValue(neutralColor);
  const deferredAtmosphereColor = useDeferredValue(atmosphereColorProp || 'rgb(115, 115, 115)');
  const deferredGlobeColor = useDeferredValue(globeColor);

  const liveRef = useRef<LiveSettings>({
    showAtmosphere,
    atmosphereColor: deferredAtmosphereColor,
    atmosphereAltitude,
    globeColor: deferredGlobeColor,
    globeOpacity,
    autoRotateSpeed,
    enableZoom,
    interactive,
    cameraAltitude,
    neutralColor: deferredNeutralColor,
  });
  liveRef.current = {
    showAtmosphere,
    atmosphereColor: deferredAtmosphereColor,
    atmosphereAltitude,
    globeColor: deferredGlobeColor,
    globeOpacity,
    autoRotateSpeed,
    enableZoom,
    interactive,
    cameraAltitude,
    neutralColor: deferredNeutralColor,
  };

  /** Find the merged points mesh and the ShaderMaterial constructor from the scene */
  type SceneRefs = {
    pointsMesh: ThreeObject3D & { material: ThreeMaterial };
    ShaderMat: ThreeShaderMaterialConstructor;
  };
  const getSceneRefs = useCallback((): SceneRefs | null => {
    const globe = globeRef.current;
    if (!globe) return null;

    const scene = globe.scene();
    let foundMesh: SceneRefs['pointsMesh'] | null = null;
    let foundShader: SceneRefs['ShaderMat'] | null = null;

    scene.traverse((obj: ThreeObject3D) => {
      const mat = obj.material;
      if (!mat || Array.isArray(mat)) return;

      if (
        obj.geometry?.type === 'BufferGeometry' &&
        mat.type === 'MeshLambertMaterial' &&
        (obj.geometry.attributes.position?.count ?? 0) > 10000
      ) {
        foundMesh = obj as SceneRefs['pointsMesh'];
      }

      if (mat.type === 'ShaderMaterial' && !foundShader) {
        foundShader = mat.constructor as unknown as ThreeShaderMaterialConstructor;
      }
    });

    if (!foundMesh || !foundShader) return null;
    return { pointsMesh: foundMesh, ShaderMat: foundShader };
  }, []);

  /** Stop any running ripple animation and reset time to 0 (no material swap) */
  const stopRipple = useCallback(() => {
    if (rippleDelayRef.current !== null) {
      clearTimeout(rippleDelayRef.current);
      rippleDelayRef.current = null;
    }
    if (rippleFrameRef.current !== null) {
      cancelAnimationFrame(rippleFrameRef.current);
      rippleFrameRef.current = null;
    }
    const shader = shaderRef.current;
    if (shader) {
      shader.uniforms.time.value = 0;
    }
  }, []);

  const applyFocus = useCallback(
    (
      coord: GlobeCoordinate,
      opts?: { altitude?: number; transitionMs?: number; color?: string }
    ) => {
      const globe = globeRef.current;
      if (!globe) return;

      stopRipple();

      const alt = opts?.altitude ?? focusAltitude;
      const duration = opts?.transitionMs ?? focusTransitionMs;
      const color = opts?.color ?? focusColor;
      const [r, g, b] = parseRgbTuple(color);

      isFocusedRef.current = true;

      globe.controls().autoRotate = false;

      const cameraLat = Math.min(coord.lat + 12, 90);
      globe.pointOfView({ lat: cameraLat, lng: coord.lng, altitude: alt }, duration);

      globe.labelsData([{ lat: coord.lat, lng: coord.lng }]);
      globe.labelText(() => '');
      globe.labelColor(() => color);
      globe.labelDotRadius(0.5);
      globe.labelAltitude(0.01);

      // Shader effects (dimming + ripple) only if shader was set up
      const shader = shaderRef.current;
      if (!shader) return;

      const { uniforms } = shader;
      const focusDir = latLngToDir(coord.lat, coord.lng);
      uniforms.focusDir.value = focusDir;
      uniforms.waveColor.value = { x: r / 255, y: g / 255, z: b / 255 };

      // Smoothly darken non-wave dots during camera transition so the wavefront pops
      if (fadeFrameRef.current !== null) cancelAnimationFrame(fadeFrameRef.current);
      const dimStart = performance.now();
      const dimFrom = uniforms.dimming.value;
      const DIM_TARGET = 0.55;
      const dimTick = (now: number) => {
        const p = Math.min((now - dimStart) / duration, 1);
        uniforms.dimming.value = dimFrom + (DIM_TARGET - dimFrom) * p;
        if (p < 1) {
          fadeFrameRef.current = requestAnimationFrame(dimTick);
        } else {
          fadeFrameRef.current = null;
        }
      };
      fadeFrameRef.current = requestAnimationFrame(dimTick);

      // Start ripple animation after camera transition completes
      rippleDelayRef.current = setTimeout(() => {
        rippleDelayRef.current = null;
        const startTime = performance.now();

        const animate = (now: number) => {
          if (!shaderRef.current) return;
          const t = (now - startTime) / 1000;
          uniforms.time.value = t;
          rippleFrameRef.current = requestAnimationFrame(animate);
        };

        rippleFrameRef.current = requestAnimationFrame(animate);
      }, duration);
    },
    [focusAltitude, focusTransitionMs, focusColor, stopRipple]
  );

  const clearFocusState = useCallback(
    (opts?: { transitionMs?: number }) => {
      const globe = globeRef.current;
      if (!globe) return;

      stopRipple();
      isFocusedRef.current = false;

      const duration = opts?.transitionMs ?? focusTransitionMs;

      globe.pointOfView({ altitude: cameraAltitude }, duration);
      globe.controls().autoRotate = true;
      globe.controls().autoRotateSpeed = autoRotateSpeed;

      globe.labelsData([]);

      // Smoothly restore dot opacity during camera pull-back
      const shader = shaderRef.current;
      if (shader) {
        if (fadeFrameRef.current !== null) cancelAnimationFrame(fadeFrameRef.current);
        const restoreStart = performance.now();
        const fromDim = shader.uniforms.dimming.value;
        const restoreTick = (now: number) => {
          const p = Math.min((now - restoreStart) / duration, 1);
          shader.uniforms.dimming.value = fromDim * (1.0 - p);
          if (p < 1) {
            fadeFrameRef.current = requestAnimationFrame(restoreTick);
          } else {
            fadeFrameRef.current = null;
          }
        };
        fadeFrameRef.current = requestAnimationFrame(restoreTick);
      }
    },
    [focusTransitionMs, cameraAltitude, autoRotateSpeed, stopRipple]
  );

  useImperativeHandle(
    ref,
    () => ({
      focusOn: applyFocus,
      clearFocus: clearFocusState,
    }),
    [applyFocus, clearFocusState]
  );

  const focusLat = focusCoordinate?.lat ?? null;
  const focusLng = focusCoordinate?.lng ?? null;

  useEffect(() => {
    if (!globeRef.current) return;

    if (focusLat !== null && focusLng !== null) {
      applyFocus({ lat: focusLat, lng: focusLng });
    } else if (isFocusedRef.current) {
      clearFocusState();
    }
  }, [focusLat, focusLng, applyFocus, clearFocusState]);

  useEffect(() => {
    loadGlobeModule()
      .then((Ctor) => setGlobeClass(() => Ctor))
      .catch((err) => {
        setError(err instanceof Error ? err.message : 'Failed to load globe.gl');
      });
  }, []);

  const processLandMap = useCallback(
    (image: HTMLImageElement): LandDot[] => {
      const cacheKey = `${landMapUrl}_${landDotRows}`;
      const cached = landDotsCache.get(cacheKey);
      if (cached) return cached;

      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      if (!ctx) return [];

      canvas.width = image.width;
      canvas.height = image.height;
      ctx.drawImage(image, 0, 0);

      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const dots: LandDot[] = [];

      const imgWidth = imageData.width;
      const imgHeight = imageData.height;
      const data = imageData.data;
      const rowBytes = 4 * imgWidth;

      const visibilityForCoordinate = (lng: number, lat: number): boolean => {
        const col = Math.floor(((lng + 180) / 360) * imgWidth + 0.5);
        const row = imgHeight - Math.floor(((lat + 90) / 180) * imgHeight - 0.5);
        const alphaIndex = Math.floor(rowBytes * (row - 1) + 4 * col) + 3;
        return data[alphaIndex] > 90;
      };

      // Scale both latitude rows and longitude dots per row so density
      // increases uniformly. The base reference (150 rows) gives the
      // original dot spacing; higher landDotRows scales proportionally.
      const densityScale = landDotRows / 150;
      const globeRad = 25;
      for (let lat = -90; lat <= 90; lat += 180 / landDotRows) {
        const radius = Math.cos(Math.abs(lat) * DEG2RAD) * globeRad;
        const circum = radius * Math.PI * 2 * 2 * densityScale;
        for (let r = 0; r < circum; r++) {
          const lng = (360 * r) / circum - 180;
          if (visibilityForCoordinate(lng, lat)) {
            dots.push({ lat, lng });
          }
        }
      }

      landDotsCache.set(cacheKey, dots);
      return dots;
    },
    [landDotRows, landMapUrl]
  );

  const cleanup = useCallback(() => {
    stopRipple();

    if (fadeFrameRef.current !== null) {
      cancelAnimationFrame(fadeFrameRef.current);
      fadeFrameRef.current = null;
    }

    const shader = shaderRef.current;
    if (shader) {
      shader.mesh.material = shader.originalMaterial;
      shader.material.dispose();
      shaderRef.current = null;
    }

    if (globeRef.current) {
      try {
        const scene = globeRef.current.scene();
        if (scene) {
          scene.traverse((object: ThreeObject3D) => {
            if (object.geometry) object.geometry.dispose();
            if (object.material) {
              if (Array.isArray(object.material)) {
                object.material.forEach(disposeMaterial);
              } else {
                disposeMaterial(object.material);
              }
            }
          });
        }
      } catch (e) {
        // Three.js cleanup errors are non-fatal; suppress in production
      }
      globeRef.current = null;
    }

    if (containerRef.current) {
      while (containerRef.current.firstChild) {
        containerRef.current.removeChild(containerRef.current.firstChild);
      }
    }

    isInitializingRef.current = false;
  }, [stopRipple]);

  // Structural init: only re-runs for props that require a full globe rebuild
  // (geometry-affecting: width, height, landMapUrl, landDotRows, pointSize, pointResolution).
  // All other props are live-updated via separate effects below.
  useEffect(() => {
    if (!GlobeClass || error || !containerRef.current) return;
    if (isInitializingRef.current) return;
    isInitializingRef.current = true;

    if (cleanupFnRef.current) {
      cleanupFnRef.current();
      cleanupFnRef.current = null;
    } else {
      cleanup();
    }

    const initGlobeDeferred = () => {
      if (!containerRef.current) {
        isInitializingRef.current = false;
        return;
      }

      const container = containerRef.current;
      const containerWidth =
        width === 'auto' ? container.parentElement?.getBoundingClientRect().width || 600 : width;
      const containerHeight = height === 'auto' ? containerWidth : height;

      const landMapImage = new Image();
      landMapImage.crossOrigin = 'anonymous';
      landMapImage.src = landMapUrl;

      landMapImage.onload = () => {
        const dots = processLandMap(landMapImage);

        const s = liveRef.current;

        const world = new GlobeClass(container)
          .globeImageUrl(createColorTexture(s.globeColor))
          .backgroundColor('rgba(0, 0, 0, 0)')
          .showAtmosphere(s.showAtmosphere)
          .atmosphereColor(s.atmosphereColor)
          .atmosphereAltitude(s.atmosphereAltitude)
          .width(containerWidth)
          .height(containerHeight)
          .pointsData(dots)
          .pointColor(() => s.neutralColor)
          .pointRadius(pointSize)
          .pointResolution(pointResolution)
          .pointAltitude(0.004)
          .pointsMerge(true);

        const globeMat = world.globeMaterial();
        globeMat.transparent = true;
        globeMat.opacity = s.globeOpacity;
        globeMat.shininess = 0.5;

        world.pointOfView({ altitude: s.cameraAltitude });
        world.controls().autoRotate = true;
        world.controls().autoRotateSpeed = s.autoRotateSpeed;
        world.controls().enabled = s.interactive;
        world.controls().enableZoom = s.enableZoom;

        world.onGlobeClick((coords: { lat: number; lng: number }, event: MouseEvent) => {
          if (onGlobeClickRef.current) {
            onGlobeClickRef.current(coords, event);
          }
        });

        globeRef.current = world;

        // Poll until globe.gl finishes creating the merged points mesh,
        // then apply our permanent shader. Typically ready within a few frames.
        let pollCount = 0;
        const pollForMesh = () => {
          const refs = getSceneRefs();
          if (!refs) {
            if (++pollCount < 120) {
              requestAnimationFrame(pollForMesh);
            } else {
              // Timed out waiting for globe.gl merged points mesh; show globe anyway
              setIsGlobeVisible(true);
            }
            return;
          }

          setIsGlobeVisible(true);

          const { pointsMesh, ShaderMat } = refs;
          const [nr, ng, nb] = parseRgbTuple(liveRef.current.neutralColor);

          const shaderMaterial = new ShaderMat({
            uniforms: {
              focusDir: { value: { x: 0, y: 1, z: 0 } },
              time: { value: 0.0 },
              sigma0: { value: 0.015 },
              dispersionLen: { value: 0.2 },
              cycleDuration: { value: 2.8 },
              maxFront: { value: 0.45 },
              waveColor: { value: { x: 0, y: 0, z: 0 } },
              waveAlpha: { value: 2.5 },
              originalColor: { value: { x: nr / 255, y: ng / 255, z: nb / 255 } },
              originalAlpha: { value: 0.0 },
              dimming: { value: 0.0 },
            },
            vertexShader: RIPPLE_VERTEX_SHADER,
            fragmentShader: RIPPLE_FRAGMENT_SHADER,
            transparent: true,
            depthWrite: true,
          });

          const typedUniforms = shaderMaterial.uniforms as unknown as RippleUniforms;
          shaderRef.current = {
            mesh: pointsMesh,
            originalMaterial: pointsMesh.material,
            material: shaderMaterial,
            uniforms: typedUniforms,
          };
          pointsMesh.material = shaderMaterial;

          // Smooth fade-in of all land dots
          const fadeStart = performance.now();
          const FADE_MS = 800;
          const fadeTick = (now: number) => {
            const alpha = Math.min((now - fadeStart) / FADE_MS, 1);
            typedUniforms.originalAlpha.value = alpha;
            if (alpha < 1) {
              fadeFrameRef.current = requestAnimationFrame(fadeTick);
            } else {
              fadeFrameRef.current = null;
            }
          };
          fadeFrameRef.current = requestAnimationFrame(fadeTick);
        };
        requestAnimationFrame(pollForMesh);

        let resizeTimeout: ReturnType<typeof setTimeout>;
        const handleResize = () => {
          clearTimeout(resizeTimeout);
          resizeTimeout = setTimeout(() => {
            if (!globeRef.current || !container.parentElement) return;
            const newWidth =
              width === 'auto' ? container.parentElement.getBoundingClientRect().width : width;
            const newHeight = height === 'auto' ? newWidth : height;
            globeRef.current.width(newWidth);
            globeRef.current.height(newHeight);
          }, 150);
        };

        window.addEventListener('resize', handleResize);

        let resizeObserver: ResizeObserver | null = null;
        if ('ResizeObserver' in window && container.parentElement) {
          resizeObserver = new ResizeObserver(handleResize);
          resizeObserver.observe(container.parentElement);
        }

        let observer: IntersectionObserver | null = null;
        if ('IntersectionObserver' in window) {
          observer = new IntersectionObserver(
            (entries) => {
              entries.forEach((entry) => {
                if (globeRef.current) {
                  globeRef.current.controls().autoRotate =
                    entry.isIntersecting && !isFocusedRef.current;
                }
              });
            },
            { threshold: 0.1 }
          );
          observer.observe(container);
        }

        const localCleanup = () => {
          window.removeEventListener('resize', handleResize);
          clearTimeout(resizeTimeout);
          if (observer) observer.disconnect();
          if (resizeObserver) resizeObserver.disconnect();
          cleanup();
        };

        cleanupFnRef.current = localCleanup;
        onReady?.();
      };

      landMapImage.onerror = () => {
        setError('Failed to load land map image');
        isInitializingRef.current = false;
      };
    };

    if ('requestIdleCallback' in window) {
      (
        window as Window & {
          requestIdleCallback: (cb: () => void, opts?: { timeout: number }) => void;
        }
      ).requestIdleCallback(initGlobeDeferred, { timeout: 500 });
    } else {
      setTimeout(initGlobeDeferred, 0);
    }

    return () => {
      if (cleanupFnRef.current) {
        cleanupFnRef.current();
        cleanupFnRef.current = null;
      }
    };
  }, [
    GlobeClass,
    error,
    width,
    height,
    landMapUrl,
    processLandMap,
    onReady,
    cleanup,
    pointSize,
    pointResolution,
    getSceneRefs,
  ]);

  // ---- Live-update effects: modify the running globe instance without rebuilding ----

  useEffect(() => {
    const globe = globeRef.current;
    if (!globe) return;
    globe.globeImageUrl(createColorTexture(deferredGlobeColor));
    const mat = globe.globeMaterial();
    mat.opacity = globeOpacity;
    mat.transparent = true;
  }, [deferredGlobeColor, globeOpacity]);

  useEffect(() => {
    const globe = globeRef.current;
    if (!globe) return;
    globe.showAtmosphere(showAtmosphere);
    globe.atmosphereColor(deferredAtmosphereColor);
    globe.atmosphereAltitude(atmosphereAltitude);
  }, [showAtmosphere, deferredAtmosphereColor, atmosphereAltitude]);

  useEffect(() => {
    const globe = globeRef.current;
    if (!globe) return;
    const controls = globe.controls();
    if (!isFocusedRef.current) {
      controls.autoRotateSpeed = autoRotateSpeed;
    }
    controls.enabled = interactive;
    controls.enableZoom = enableZoom;
  }, [autoRotateSpeed, enableZoom, interactive]);

  useEffect(() => {
    const globe = globeRef.current;
    if (!globe || isFocusedRef.current) return;
    globe.pointOfView({ altitude: cameraAltitude });
  }, [cameraAltitude]);

  useEffect(() => {
    const shader = shaderRef.current;
    if (!shader) return;
    const [r, g, b] = parseRgbTuple(deferredNeutralColor);
    shader.uniforms.originalColor.value = { x: r / 255, y: g / 255, z: b / 255 };
  }, [deferredNeutralColor]);

  if (error) {
    return (
      <div
        className={cn(
          'flex items-center justify-center rounded-lg border border-red-200 bg-red-50 p-8 text-red-600',
          className
        )}
      >
        <p>Error loading globe: {error}</p>
      </div>
    );
  }

  if (!GlobeClass) return null;

  return (
    <div
      ref={containerRef}
      className={cn(
        'relative overflow-hidden',
        interactive ? 'cursor-grab' : 'cursor-default',
        className
      )}
      style={{
        width: width === 'auto' ? '100%' : width,
        height: height === 'auto' ? 'auto' : height,
        opacity: isGlobeVisible ? 1 : 0,
        transform: isGlobeVisible ? 'scale(1)' : 'scale(0.85)',
        transition: 'opacity 0.8s ease-out, transform 0.8s cubic-bezier(0.34, 1.56, 0.64, 1)',
      }}
    />
  );
});

Globe.displayName = 'Globe';
